import React from 'react';
import { groupBy, keyBy, sortBy, fromPairs, orderBy, omit } from 'lodash';
import { format as formatDate, eachDayOfInterval, startOfDay, endOfDay, subDays } from 'date-fns';
import numeral from 'numeral';
import { BarChart, Bar, CartesianGrid, XAxis, YAxis, Tooltip, Legend } from 'recharts';

import firebase from '../../firebase';
import { colors } from '../../shared/config';
import { activityTypes as inquiryActivityTypes } from '../../shared/models/inquiry';
import { activityTypes as troubleInquiryActivityTypes } from '../../shared/models/troubleInquiry';
import { activityTypes as methodInquiryActivityTypes } from '../../shared/models/methodInquiry';
import { activityTypes as surveyAnswerActivityTypes } from '../../shared/models/surveyAnswer';
import { Button } from 'reactstrap';
import TenantLink from '../TenantLink';
import AdminPage from '../hocs/AdminPage';
import useCollectionSubscriptionInTenant from '../hooks/useCollectionSubscriptionInTenant';
import useCollectionFetchInTenant from '../hooks/useCollectionFetchInTenant';
import useQueryParams from '../hooks/useQueryParams';
import QuerySelector from '../QuerySelector';
import QueryDateSelector from '../QueryDateSelector';
import { fullPathWithParams } from '../../util';

const { entries } = Object;
const db = firebase.firestore();
const activitiesRef = db.collection('activities');
const usersRef = db.collection('users');

const activityTypes = {
  inquiry: { label: 'お問合せ', activityTypes: inquiryActivityTypes },
  troubleInquiry: { label: '不具合組立', activityTypes: troubleInquiryActivityTypes },
  methodInquiry: { label: '乗り方問合せ', activityTypes: methodInquiryActivityTypes },
  surveyAnswer: { label: 'アンケート', activityTypes: surveyAnswerActivityTypes },
};

// AIが自動で作るアクティビティはスタッフの活動ではないので、この画面の集計からは外す
const aiActivityTypes = ['generateAiDraftReplyOfInquiry', 'generateAiDraftReplyOfTroubleInquiry'];

const allActivityTypes = omit(
  Object.assign({}, ...Object.values(activityTypes).map(_ => _.activityTypes)),
  aiActivityTypes,
);

const activityLink = (payload) => {
  if (!payload) return null;
  if (payload.inquiryId) return `/admin/inquiries/${payload.inquiryId}`;
  if (payload.troubleInquiryId) return `/admin/troubleInquiries/${payload.troubleInquiryId}`;
  if (payload.methodInquiryId) return `/admin/methodInquiries/${payload.methodInquiryId}`;
  if (payload.surveyAnswer?.id) return `/admin/surveyAnswers/${payload.surveyAnswer.id}`;
  return null;
};

const activityTypeLabels = fromPairs(
  entries(activityTypes).flatMap(([, { label, activityTypes: types }]) =>
    entries(omit(types, aiActivityTypes)).map(([k, v]) => [k, `${label} - ${v.text()}`])
  ),
);

const activityTypeOptions = sortBy(
  entries(activityTypeLabels).map(([k, label]) => ({ label, value: k })),
  'label',
);

const now = new Date();

export default AdminPage(function AdminStaffActivities(props) {
  const { user } = props;
  const history = props.history;
  const location = props.location;
  const queryParams = useQueryParams();
  const {
    activityType: selectedActivityTypes,
    staff: selectedStaffs,
    startOn: startOnString = formatDate(subDays(now, 60), 'yyyy-MM-dd'),
    endOn: endOnString = formatDate(now, 'yyyy-MM-dd'),
  } = queryParams;
  const startOn = new Date(startOnString);
  const endOn = new Date(endOnString);
  const onClickDateButton = ([s, e]) => {
    const path = fullPathWithParams(
      { startOn: formatDate(s, 'yyyy-MM-dd'), endOn: formatDate(e, 'yyyy-MM-dd') },
      location,
    );
    history.replace(encodeURI(path));
  };

  const staffs = useCollectionSubscriptionInTenant(usersRef.where('role', 'in', ['admin', 'staff']));
  const staffsById = keyBy(staffs, 'id');
  const staffOptions = sortBy(staffs.map(_ => ({ label: _.displayName, value: _.id })), 'label');

  const allActivities = useCollectionFetchInTenant(
    activitiesRef
      .where('createdAt', '>=', startOfDay(startOn))
      .where('createdAt', '<=', endOfDay(endOn)),
    [startOnString, endOnString],
  );

  const selectedTypes = selectedActivityTypes?.length > 0
    ? [].concat(selectedActivityTypes)
    : null;
  const selectedStaffIds = selectedStaffs?.length > 0
    ? [].concat(selectedStaffs)
    : null;

  const activities = allActivities
    .filter(_ => allActivityTypes[_.type])
    .filter(_ => !selectedTypes || selectedTypes.includes(_.type))
    .filter(_ => !selectedStaffIds || selectedStaffIds.includes(_.createdBy?.uid));

  const activitiesByDate = groupBy(activities, _ => formatDate(_.createdAt.toDate(), 'yyyyMMdd'));

  const activitiesGroupedByStaff = groupBy(activities, _ => _.createdBy?.uid);
  const staffActivityCounts = sortBy(
    staffs
      .map(_ => ({
        staff: _,
        total: (activitiesGroupedByStaff[_.id] || []).length,
      }))
      .filter(_ => _.total > 0),
    _ => -_.total,
  );

  const activeStaffIds = staffActivityCounts.map(_ => _.staff.id);
  const staffNames = activeStaffIds.map(id => staffsById[id]?.displayName || id);

  const xs = eachDayOfInterval({ start: startOn, end: endOn }).map(_ => [_]);
  const timeSeriesData = xs.map((dates) => {
    const rows = dates.flatMap(_ => activitiesByDate[formatDate(_, 'yyyyMMdd')] || []);
    return {
      x: formatDate(dates[0], 'MM/dd'),
      ...fromPairs(activeStaffIds.map(id => [
        staffsById[id]?.displayName || id,
        rows.filter(_ => _.createdBy?.uid === id).length,
      ])),
    };
  });

  const sortedActivities = orderBy(activities, _ => _.createdAt.toDate(), 'desc');

  const chartWidth = 1500;
  const chartHeight = 380;

  return (
    <div>
      <div className="admin-events container-fluid py-5">
        <div className="d-flex justify-content-center mb-3">
          <h4>スタッフ活動状況</h4>
        </div>
        <div className="d-flex align-items-end gap-2 mb-3">
          <Button onClick={onClickDateButton.bind(null, [subDays(now, 60), now])}>過去60日</Button>
          <Button onClick={onClickDateButton.bind(null, [subDays(now, 180), now])}>過去半年</Button>
          <Button onClick={onClickDateButton.bind(null, [subDays(now, 365), now])}>過去1年</Button>
          <QueryDateSelector
            paramName='startOn'
            label='開始日'
            history={history}
            location={location}
            defaultValue={subDays(now, 60)}
            style={{ width: 150 }}
          />
          <QueryDateSelector
            paramName='endOn'
            label='終了日'
            history={history}
            location={location}
            defaultValue={now}
            invalid={startOn > endOn}
            style={{ width: 150 }}
          />
          <QuerySelector paramName="activityType" options={activityTypeOptions} label="アクティビティ種別" isMulti width={400} />
          <QuerySelector paramName="staff" options={staffOptions} label="スタッフ" isMulti width={300} />
        </div>

        <div className="card mb-4">
          <div className="card-header">期間内アクティビティ集計（{formatDate(startOn, 'yyyy/MM/dd')} 〜 {formatDate(endOn, 'yyyy/MM/dd')}）</div>
          <div className="card-body p-0">
            <table className="table table-sm table-hover mb-0">
              <thead>
                <tr>
                  <th>スタッフ</th>
                  <th className="text-right">件数</th>
                </tr>
              </thead>
              <tbody>
                {staffActivityCounts.map(({ staff, total }) => (
                  <tr key={staff.id}>
                    <td>{staff.displayName}</td>
                    <td className="text-right font-weight-bold">{numeral(total).format('0,0')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="card mb-4">
          <div className="card-header">スタッフ別アクティビティ推移</div>
          <div className="card-body d-flex justify-content-center">
            <BarChart width={chartWidth} height={chartHeight} data={timeSeriesData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="x" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip />
              <Legend />
              {staffNames.map((name, i) => (
                <Bar key={name} dataKey={name} stackId="a" fill={colors[i % colors.length]} />
              ))}
            </BarChart>
          </div>
        </div>

        <div className="card mb-4">
          <div className="card-header">アクティビティタイムライン</div>
          <div className="card-body p-0">
            <table className="table table-sm table-hover mb-0">
              <thead>
                <tr>
                  <th style={{ width: 150 }}>日時</th>
                  <th style={{ width: 120 }}>スタッフ</th>
                  <th>アクティビティ</th>
                </tr>
              </thead>
              <tbody>
                {sortedActivities.map(_ => {
                  const link = activityLink(_.payload);
                  return (
                    <tr key={_.id}>
                      <td className="text-muted small">{formatDate(_.createdAt.toDate(), 'yyyy/MM/dd HH:mm')}</td>
                      <td>{_.createdBy?.displayName}</td>
                      <td>
                        {link
                          ? <TenantLink to={link}>{activityTypeLabels[_.type] || _.type}</TenantLink>
                          : (activityTypeLabels[_.type] || _.type)
                        }
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
});
